/**
 * T148 — /suppliers/$id detail + edit.
 *
 * Read + edit + linked-expenses list. Uses optimistic-lock version field
 * from the supplier DTO when writing back.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  Button,
  FormSaveBar,
  Input,
  Select,
  LoadingSpinner,
  EmptyState,
  GlassPanel,
  Modal,
  PageHeader,
} from '../components/ui';
import {
  useSupplier,
  useUpdateSupplier,
  useArchiveSupplier,
  useUnarchiveSupplier,
} from '../services/api/suppliers';
import { useExpenses } from '../services/api/expenses';
import { useCategories } from '../services/api/categories';
import { useToastHelpers } from '../components/ui/Toast';
import { formatCurrency, type UpdateSupplierInput } from '@armard/shared';
import { formatDateBerlin, formatDateTimeBerlin } from '../lib/format-date';
import { useUnsavedChanges, markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';

export const Route = createFileRoute('/suppliers_/$id')({
  component: SupplierDetailPage,
});

function SupplierDetailPage() {
  const { id } = Route.useParams();
  return (
    <ProtectedRoute>
      <AppShell>
        <SupplierDetailInner key={id} id={id} />
      </AppShell>
    </ProtectedRoute>
  );
}

function SupplierDetailInner({ id }: { id: string }) {
  const { data: supplier, isLoading, error } = useSupplier(id);
  const update = useUpdateSupplier(id);
  const archive = useArchiveSupplier(id);
  const unarchive = useUnarchiveSupplier(id);
  const toast = useToastHelpers();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editVersion, setEditVersion] = useState<number | undefined>(undefined);
  const [confirmCancelEdit, setConfirmCancelEdit] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [vatId, setVatId] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [defaultEmailDomain, setDefaultEmailDomain] = useState('');
  const [kleinunternehmer, setKleinunternehmer] = useState(false);
  const [defaultAccountOverride, setDefaultAccountOverride] = useState('');
  const [defaultPaymentDays, setDefaultPaymentDays] = useState<string>('');
  const [notes, setNotes] = useState('');
  // T571b — IBAN/BIC editable so an edit (which also flips
  // resolution_state 'auto' → 'confirmed' on the backend) is reachable
  // from the UI.
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  // T571b expansion — default category + additional allowlist emails
  // editable from the same form. Categories list is fetched up front so
  // the dropdown render isn't gated on edit-mode entry.
  const [defaultCategoryId, setDefaultCategoryId] = useState<string>('');
  const [additionalAllowedEmailsText, setAdditionalAllowedEmailsText] = useState<string>('');

  const editDraft = {
    displayName,
    vatId,
    taxNumber,
    defaultEmailDomain,
    kleinunternehmer,
    defaultAccountOverride,
    defaultPaymentDays,
    notes,
    iban,
    bic,
    defaultCategoryId,
    additionalAllowedEmailsText,
  };
  const editGuard = useUnsavedChanges(editDraft, undefined, isEditing);
  const editBaselinePending = useRef(false);
  useEffect(() => {
    if (!isEditing || !editBaselinePending.current) return;
    editGuard.markSaved();
    editBaselinePending.current = false;
  }, [editGuard.isDirty, editGuard.markSaved, isEditing]);

  const { data: linkedExpenses } = useExpenses({
    supplierId: supplier?.id,
    limit: 50,
  });
  const { data: categoriesData } = useCategories({ limit: 200 });

  function seedEditForm() {
    if (!supplier) return;
    setEditVersion(supplier.version);
    setDisplayName(supplier.displayName);
    setVatId(supplier.vatId ?? '');
    setTaxNumber(supplier.taxNumber ?? '');
    setDefaultEmailDomain(supplier.defaultEmailDomain ?? '');
    setKleinunternehmer(supplier.kleinunternehmer);
    setDefaultAccountOverride(supplier.defaultAccountOverride ?? '');
    setDefaultPaymentDays(
      supplier.defaultPaymentDays != null ? String(supplier.defaultPaymentDays) : ''
    );
    setNotes(supplier.notes ?? '');
    setIban(supplier.iban ?? '');
    setBic(supplier.bic ?? '');
    setDefaultCategoryId(supplier.defaultCategoryId ?? '');
    setAdditionalAllowedEmailsText(supplier.additionalAllowedEmails.join(', '));
  }

  function beginEdit() {
    seedEditForm();
    editBaselinePending.current = true;
    setIsEditing(true);
  }

  function cancelEdit() {
    seedEditForm();
    editBaselinePending.current = true;
    setIsEditing(false);
  }

  function requestCancelEdit() {
    if (editGuard.isDirty) setConfirmCancelEdit(true);
    else cancelEdit();
  }

  // T571b — derived stats from currently-loaded linked expenses. Backend
  // YTD aggregation is a follow-up; for now we use what /expenses already
  // returns (cap-50 list) and label it accordingly so users know the
  // numbers reflect the visible subset rather than a full-history total.
  const stats = useMemo(() => {
    if (!linkedExpenses) return null;
    const items = linkedExpenses.items;
    if (items.length === 0) {
      return { count: 0, total: 0, avg: 0, ytdTotal: 0, ytdCount: 0 };
    }
    const toNum = (v: string | number | null | undefined): number => {
      if (v == null) return 0;
      if (typeof v === 'number') return v;
      const parsed = Number(String(v).replace(/,/g, '.'));
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const sum = items.reduce((acc, e) => acc + toNum(e.grossTotal), 0);
    const year = new Date().getFullYear();
    const ytdItems = items.filter(
      (e) => typeof e.invoiceDate === 'string' && e.invoiceDate.startsWith(String(year))
    );
    const ytdTotal = ytdItems.reduce((acc, e) => acc + toNum(e.grossTotal), 0);
    return {
      count: items.length,
      total: sum,
      avg: sum / items.length,
      ytdTotal,
      ytdCount: ytdItems.length,
    };
  }, [linkedExpenses]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <EmptyState
        title="Lieferant nicht gefunden"
        description={(error as Error | undefined)?.message ?? undefined}
      />
    );
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!supplier) return;
    const additionalEmails = additionalAllowedEmailsText
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const payload: UpdateSupplierInput = {
      displayName,
      vatId: vatId || null,
      taxNumber: taxNumber || null,
      defaultEmailDomain: defaultEmailDomain || null,
      kleinunternehmer,
      defaultAccountOverride: defaultAccountOverride || null,
      defaultPaymentDays: defaultPaymentDays ? Number(defaultPaymentDays) : null,
      notes: notes || null,
      iban: iban || null,
      bic: bic || null,
      defaultCategoryId: defaultCategoryId || null,
      additionalAllowedEmails: additionalEmails,
      version: editVersion ?? supplier.version,
    };
    try {
      await update.mutateAsync(payload);
      markFormSaved();
      editGuard.markSaved();
      toast.success('Lieferant aktualisiert');
      setIsEditing(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Lieferant konnte nicht gespeichert werden.'));
    }
  }

  return (
    <div className="max-w-5xl space-y-6">
      <PageHeader
        backTo="/suppliers"
        backLabel="Lieferanten"
        title={supplier.displayName}
        titleAccessory={
          /* T571b — resolution-state badge + timestamp. Enum values
              per packages/db/src/schema/suppliers.ts: 'auto' | 'manuell'
              | 'confirmed'. Timestamp is updatedAt (covers "auto-created
              at X" and "confirmed at X" — both are mutations of the
              row); for 'auto' it equals createdAt so the label still
              reads correctly. UI labels stay German; data-testid uses
              raw enum so smoketest assertions don't depend on label
              translation. */
          <ResolutionStateBadge
            state={supplier.resolutionState}
            timestamp={supplier.updatedAt}
            createdAt={supplier.createdAt}
          />
        }
        subtitle={
          <>
            {supplier.isArchived ? 'archiviert' : 'aktiv'}
            {supplier.kleinunternehmer && ' · §19 UStG Kleinunternehmer'}
          </>
        }
        actions={
          <div className="flex gap-2">
            <Link to="/suppliers">
              <Button variant="ghost">Zurück</Button>
            </Link>
            {!isEditing && (
              <Button onClick={beginEdit} data-testid="supplier-edit-toggle">
                Bearbeiten
              </Button>
            )}
            {/* T-G5-fe — archive/unarchive toggle, independent of
                resolution_state. Two dimensions, two buttons. */}
            {!isEditing && supplier && !supplier.isArchived && (
              <Button
                variant="ghost"
                onClick={async () => {
                  try {
                    await archive.mutateAsync(supplier.version);
                    toast.success('Lieferant archiviert');
                  } catch (err) {
                    toast.error(`Archivieren fehlgeschlagen: ${(err as Error).message}`);
                  }
                }}
                data-testid="supplier-archive-toggle"
                disabled={archive.isPending}
              >
                {archive.isPending ? 'Archiviere…' : 'Archivieren'}
              </Button>
            )}
            {!isEditing && supplier && supplier.isArchived && (
              <Button
                variant="ghost"
                onClick={async () => {
                  try {
                    await unarchive.mutateAsync(supplier.version);
                    toast.success('Lieferant aktiviert');
                  } catch (err) {
                    toast.error(`Aktivieren fehlgeschlagen: ${(err as Error).message}`);
                  }
                }}
                data-testid="supplier-unarchive-toggle"
                disabled={unarchive.isPending}
              >
                {unarchive.isPending ? 'Aktiviere…' : 'Wieder aktivieren'}
              </Button>
            )}
          </div>
        }
      />

      {!isEditing ? (
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white/5 rounded border border-white/10 text-white/80"
          data-testid="supplier-detail-view"
        >
          <Field label="USt-ID" value={supplier.vatId ?? '—'} mono />
          <Field label="Steuernummer" value={supplier.taxNumber ?? '—'} mono />
          <Field label="Standard-Email-Domain" value={supplier.defaultEmailDomain ?? '—'} mono />
          <Field
            label="Zusätzliche Allowlist-E-Mails"
            value={
              supplier.additionalAllowedEmails.length > 0
                ? supplier.additionalAllowedEmails.join(', ')
                : '—'
            }
            mono
          />
          <Field
            label="Default-Konto-Override"
            value={supplier.defaultAccountOverride ?? '—'}
            mono
          />
          <Field
            label="Default-Kategorie"
            value={
              categoriesData?.items.find((c) => c.id === supplier.defaultCategoryId)?.name ?? '—'
            }
          />
          <Field label="IBAN" value={supplier.iban ?? '—'} mono />
          <Field label="BIC" value={supplier.bic ?? '—'} mono />
          <Field
            label="Default-Zahlungsziel (Tage)"
            value={supplier.defaultPaymentDays != null ? String(supplier.defaultPaymentDays) : '—'}
          />
          <Field
            label="Kleinunternehmer (§19 UStG)"
            value={supplier.kleinunternehmer ? 'Ja' : 'Nein'}
          />
          <Field label="Version (optim. Lock)" value={String(supplier.version)} />
          {supplier.notes && (
            <div className="md:col-span-2">
              <div className="text-xs text-white/50 mb-1">Notizen</div>
              <div className="whitespace-pre-wrap">{supplier.notes}</div>
            </div>
          )}
        </div>
      ) : (
        <form
          onSubmit={onSave}
          className="p-4 bg-white/5 rounded border border-white/10"
          data-testid="supplier-edit-form"
        >
          <fieldset
            disabled={update.isPending}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 border-0 p-0 m-0 min-w-0"
          >
            <Input
              label="Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
            <Input
              label="USt-ID"
              value={vatId}
              onChange={(e) => setVatId(e.target.value)}
              placeholder="DE123456789"
            />
            <Input
              label="Steuernummer"
              value={taxNumber}
              onChange={(e) => setTaxNumber(e.target.value)}
            />
            <Input
              label="Standard-Email-Domain"
              value={defaultEmailDomain}
              onChange={(e) => setDefaultEmailDomain(e.target.value)}
              placeholder="example.com"
            />
            <Input
              label="Default-Konto-Override (SKR)"
              value={defaultAccountOverride}
              onChange={(e) => setDefaultAccountOverride(e.target.value)}
            />
            <Input
              label="IBAN"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="DE…"
              data-testid="supplier-edit-iban"
            />
            <Input
              label="BIC"
              value={bic}
              onChange={(e) => setBic(e.target.value)}
              placeholder="GENODE…"
              data-testid="supplier-edit-bic"
            />
            <Input
              label="Default-Zahlungsziel (Tage)"
              type="number"
              min="0"
              value={defaultPaymentDays}
              onChange={(e) => setDefaultPaymentDays(e.target.value)}
            />
            <div>
              <Select
                label="Default-Kategorie"
                value={defaultCategoryId}
                onChange={(e) => setDefaultCategoryId(e.target.value)}
                data-testid="supplier-edit-default-category"
                options={[
                  { value: '', label: '— keine —' },
                  ...(categoriesData?.items ?? [])
                    .filter((c) => !c.isArchived)
                    .map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white/70 mb-1.5">
                Zusätzliche Allowlist-E-Mails (Komma- oder Whitespace-getrennt)
              </label>
              <Input
                value={additionalAllowedEmailsText}
                onChange={(e) => setAdditionalAllowedEmailsText(e.target.value)}
                placeholder="alice@example.com, bob@example.com"
                data-testid="supplier-edit-additional-emails"
              />
            </div>
            <label className="flex items-center gap-2 text-white/80 md:col-span-2">
              <input
                type="checkbox"
                checked={kleinunternehmer}
                onChange={(e) => setKleinunternehmer(e.target.checked)}
              />
              Kleinunternehmer (§19 UStG — keine MwSt auf Rechnung)
            </label>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white/70 mb-1.5">Notizen</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white"
              />
            </div>
            {/* 006b P2: die eine Save-Bar (PATTERNS.md §3) */}
            <FormSaveBar className="md:col-span-2">
              <Button type="button" variant="ghost" onClick={requestCancelEdit}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Speichern…' : 'Speichern'}
              </Button>
            </FormSaveBar>
          </fieldset>
          <Modal
            isOpen={confirmCancelEdit}
            onClose={() => {
              if (!update.isPending) setConfirmCancelEdit(false);
            }}
            title="Änderungen verwerfen?"
          >
            <p className="text-white/70 mb-6">Ungespeicherte Lieferantendaten gehen verloren.</p>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmCancelEdit(false)}
                disabled={update.isPending}
              >
                Weiter bearbeiten
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={update.isPending}
                onClick={() => {
                  setConfirmCancelEdit(false);
                  cancelEdit();
                }}
              >
                Änderungen verwerfen
              </Button>
            </div>
          </Modal>
        </form>
      )}

      {/* T571b — Statistik-Section. Numbers reflect the currently
          loaded supplier-belege subset (cap 50). A backend YTD-aggregate
          endpoint is a future enhancement; the labelled "(Aktuelle
          Sicht)" makes the scope obvious so users don't read this as
          full-history. */}
      {stats && stats.count > 0 && (
        <GlassPanel
          className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4"
          data-testid="supplier-stats"
        >
          <Stat label="Belege (Aktuelle Sicht)" value={String(stats.count)} />
          <Stat label="Σ Brutto (Aktuelle Sicht)" value={formatCurrency(stats.total)} />
          <Stat label="ø Brutto (Aktuelle Sicht)" value={formatCurrency(stats.avg)} />
          <Stat
            label={`YTD ${new Date().getFullYear()} (${stats.ytdCount})`}
            value={formatCurrency(stats.ytdTotal)}
            testid="supplier-stat-ytd"
          />
        </GlassPanel>
      )}

      {/* T561 (FR-UI-001 REFINED): wrap the sub-list in <GlassPanel> so
          the detail-page surface stays consistent with the rest of the
          spatial-glass framing (Lieferant-Detail "Verlinkte Belege"). */}
      <GlassPanel className="p-6">
        <h2 className="text-lg font-medium text-white mb-2">
          Verlinkte Belege
          {linkedExpenses && (
            <span className="text-white/50 text-sm ml-2">({linkedExpenses.pagination.total})</span>
          )}
        </h2>
        {linkedExpenses && linkedExpenses.items.length > 0 ? (
          <table className="min-w-full divide-y divide-white/10">
            <thead>
              <tr className="text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                <th className="px-4 py-3">Rechnungsnummer</th>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3 text-right">Brutto</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {linkedExpenses.items.map((e) => (
                <tr
                  key={e.id}
                  className="hover:bg-white/5 cursor-pointer"
                  onClick={() => navigate({ to: '/expenses/$id', params: { id: e.id } })}
                >
                  <td className="px-4 py-3 text-white font-mono text-sm">
                    {e.supplierInvoiceNumber ?? e.id.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3 text-white/70">
                    {/* Spec 004 FR-G1-DATE-RENDER-AUDIT */}
                    {e.invoiceDate ? (
                      formatDateBerlin(e.invoiceDate)
                    ) : (
                      <span className="text-orange-300/70 text-xs">Datum fehlt</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/80 text-right tabular-nums">
                    {formatCurrency(Number(e.grossTotal))}
                  </td>
                  <td className="px-4 py-3 text-white/70">{e.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            title="Keine Belege"
            description="Dieser Lieferant hat noch keine erfassten Belege."
          />
        )}
      </GlassPanel>
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-white/50 mb-1">{label}</div>
      <div className={mono ? 'font-mono text-sm' : ''}>{value}</div>
    </div>
  );
}

function Stat({ label, value, testid }: { label: string; value: string; testid?: string }) {
  return (
    <div data-testid={testid} className="flex flex-col">
      <span className="text-[11px] text-white/50 uppercase tracking-wider">{label}</span>
      <span className="text-lg text-white tabular-nums font-light mt-1">{value}</span>
    </div>
  );
}

function ResolutionStateBadge({
  state,
  timestamp,
  createdAt,
}: {
  state: 'auto' | 'manuell' | 'confirmed';
  timestamp: string;
  createdAt: string;
}) {
  const cls =
    state === 'auto'
      ? 'bg-white/10 text-white/70 border-white/20'
      : state === 'confirmed'
        ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
        : 'bg-sky-500/15 text-sky-200 border-sky-500/40';
  const label =
    state === 'auto'
      ? 'Auto erkannt'
      : state === 'confirmed'
        ? 'Manuell bestätigt'
        : 'Manuell angelegt';
  // 'auto' uses createdAt (no later confirmation event yet); the other
  // two use updatedAt — that's when the manual action happened.
  const stampIso = state === 'auto' ? createdAt : timestamp;
  const stamp = formatDateTimeBerlin(stampIso);
  return (
    <span
      data-testid={`supplier-resolution-state-${state}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium border ${cls}`}
    >
      {label}
      <span
        data-testid="supplier-resolution-timestamp"
        className="text-[10px] opacity-70 font-normal"
      >
        · {stamp}
      </span>
    </span>
  );
}
